import type { Lang } from "./index";

// Auth-flow copy (sign-in, sign-up, forgot/reset password, verify email,
// onboarding, invite accept) in all three shipped languages. Each language
// is written natively (grammar, idiom, tone) — not a literal translation.

type Feature = { num: string; title: string; sub: string };

export type AuthCopy = {
  // --- sign-in / sign-up screen ---
  langFieldLabel: string;
  eyebrowSignUp: string;
  eyebrowSignIn: string;
  headingSignUp: string;
  headingSignIn: string;
  nameLabel: string;
  namePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  passwordLabel: string;
  passwordHint: string;
  inviteCodeLabel: string;
  inviteCodeHint: string;
  inviteCodePlaceholder: string;
  forgotPasswordLink: string;
  acceptTermsPrefix: string;
  acceptTermsPrivacy: string;
  acceptTermsAnd: string;
  acceptTermsTerms: string;
  acceptTermsSuffix: string;
  mustAcceptTermsError: string;
  pendingVerification: string;
  genericError: string;
  submitting: string;
  submitSignUp: string;
  submitSignIn: string;
  haveAccountQuestion: string;
  noAccountQuestion: string;
  switchToSignIn: string;
  switchToSignUp: string;
  footerPrivacy: string;
  footerTerms: string;

  // --- forgot-password screen ---
  fpBackToSignIn: string;
  fpEyebrow: string;
  fpHeading: string;
  fpIntro: string;
  fpSentEyebrow: string;
  fpSentHeading: string;
  fpSentBodyPrefix: string;
  fpSentBodySuffix: string;
  fpHintLabel: string;
  fpHintBody: string;
  fpSubmitting: string;
  fpSubmit: string;
  fpBackButton: string;

  // --- reset-password screen ---
  rpEyebrow: string;
  rpHeading: string;
  rpIntro: string;
  rpNewPasswordLabel: string;
  rpNewPasswordHint: string;
  rpConfirmLabel: string;
  rpPasswordMismatch: string;
  rpSubmitting: string;
  rpSubmit: string;
  rpHintLabel: string;
  rpHintBody: string;

  // --- verify-email screen ---
  veEyebrowVerifying: string;
  veEyebrowOk: string;
  veEyebrowError: string;
  veHeadingVerifying: string;
  veHeadingOk: string;
  veHeadingError: string;
  veBodyVerifying: string;
  veBodyOk: string;
  veBodyErrorFallback: string;
  veCodeInvalidFallback: string;
  veRetryButton: string;

  // --- onboarding screen ---
  obEyebrow: string;
  obHeadingLine1: string;
  obHeadingLine2: string;
  obHeadingLine3: string;
  obIntro: string;
  obFeatures: [Feature, Feature, Feature];
  obHouseholdEyebrow: string;
  obHouseholdIntro: string;
  obNameLabel: string;
  obNamePlaceholder: string;
  obSubmitting: string;
  obSubmit: string;
  obSignOut: string;
  obFooter: string;

  // --- invite-accept screen ---
  iaHeadingLine1: string;
  iaHeadingLine2: string;
  iaIntro: string;
  iaCodeLabel: string;
  iaCardBody: string;
  iaJoinError: string;
  iaSubmitting: string;
  iaSubmit: string;
  iaLater: string;
};

const pl: AuthCopy = {
  langFieldLabel: "Język · Language · Sprache",
  eyebrowSignUp: "REJESTRACJA",
  eyebrowSignIn: "LOGOWANIE",
  headingSignUp: "Witamy w syto",
  headingSignIn: "Cześć z powrotem",
  nameLabel: "Imię",
  namePlaceholder: "np. Marek",
  emailLabel: "Email",
  emailPlaceholder: "ty@example.com",
  passwordLabel: "Hasło",
  passwordHint: "Minimum 8 znaków",
  inviteCodeLabel: "Kod zaproszeniowy",
  inviteCodeHint: "Sytofood w fazie zamkniętej beta. Kod dostajesz osobiście.",
  inviteCodePlaceholder: "np. SYTO-2026-ALPHA",
  forgotPasswordLink: "Nie pamiętam hasła →",
  acceptTermsPrefix: "Akceptuję ",
  acceptTermsPrivacy: "Politykę prywatności",
  acceptTermsAnd: " i ",
  acceptTermsTerms: "Regulamin",
  acceptTermsSuffix: ". Wiem, że Sytofood nie zastępuje porady dietetyka.",
  mustAcceptTermsError: "Musisz zaakceptować Politykę prywatności i Regulamin.",
  pendingVerification:
    "Jeśli email jest nowy, wysłaliśmy link weryfikacyjny. Jeśli masz już konto, zaloguj się.",
  genericError: "Coś nie wyszło",
  submitting: "Chwilę...",
  submitSignUp: "Załóż konto →",
  submitSignIn: "Zaloguj się →",
  haveAccountQuestion: "Masz już konto? ",
  noAccountQuestion: "Nie masz konta? ",
  switchToSignIn: "Zaloguj się",
  switchToSignUp: "Zarejestruj się",
  footerPrivacy: "POLITYKA",
  footerTerms: "REGULAMIN",

  fpBackToSignIn: "Logowanie",
  fpEyebrow: "RESETOWANIE HASŁA",
  fpHeading: "Wyślemy link",
  fpIntro: "Podaj email, na który założyłaś konto. Link działa 30 minut.",
  fpSentEyebrow: "SPRAWDŹ POCZTĘ",
  fpSentHeading: "Wysłane",
  fpSentBodyPrefix: "Jeśli mamy takie konto, link do resetu poleciał na ",
  fpSentBodySuffix: ". Działa 30 minut.",
  fpHintLabel: "WSKAZÓWKA",
  fpHintBody: "Sprawdź też spam, czasem zaplącze się tam.",
  fpSubmitting: "Wysyłam...",
  fpSubmit: "Wyślij link →",
  fpBackButton: "Wróć do logowania →",

  rpEyebrow: "NOWE HASŁO",
  rpHeading: "Ustaw świeże",
  rpIntro: "Minimum 8 znaków. Po zapisaniu od razu Cię zalogujemy.",
  rpNewPasswordLabel: "Nowe hasło",
  rpNewPasswordHint: "Minimum 8 znaków",
  rpConfirmLabel: "Powtórz hasło",
  rpPasswordMismatch: "Hasła się różnią. Sprawdź jeszcze raz.",
  rpSubmitting: "Zapisuję...",
  rpSubmit: "Ustaw hasło →",
  rpHintLabel: "WSKAZÓWKA",
  rpHintBody: "Dłuższe i niesłownikowe wygrywają z trudnymi do zapamiętania.",

  veEyebrowVerifying: "SPRAWDZAMY KOD",
  veEyebrowOk: "EMAIL ZWERYFIKOWANY",
  veEyebrowError: "KOD NIEPRAWIDŁOWY",
  veHeadingVerifying: "Chwilę",
  veHeadingOk: "Wszystko gotowe",
  veHeadingError: "Coś nie pyknęło",
  veBodyVerifying: "Łączymy się z serwerem. Sekunda, najwyżej dwie.",
  veBodyOk: "Twoje konto jest aktywne. Wracamy do apki.",
  veBodyErrorFallback: "Kod nieprawidłowy lub wygasł. Spróbuj jeszcze raz.",
  veCodeInvalidFallback: "Kod nieprawidłowy lub wygasł",
  veRetryButton: "Wróć →",

  obEyebrow: "POWITANIE · 1/1",
  obHeadingLine1: "Jesz.",
  obHeadingLine2: "Mówisz.",
  obHeadingLine3: "My liczymy",
  obIntro:
    "Wpisz, sfotografuj albo powiedz co jesz. AI policzy kcal i makro. Bez wagi, bez tabelek, bez ED-vibe.",
  obFeatures: [
    { num: "01", title: "Zdjęcie lodówki", sub: "wykryjemy 30+ produktów" },
    { num: "02", title: "Skan menu", sub: "top 3 dla Twojego budżetu" },
    { num: "03", title: '"Mam ochotę na…"', sub: "przepis pod resztę dnia" },
  ],
  obHouseholdEyebrow: "TWOJE GOSPODARSTWO",
  obHouseholdIntro:
    "Sytofood działa per household. Stwórz swoje, potem zaproś rodzinę.",
  obNameLabel: "Nazwa",
  obNamePlaceholder: "np. Kazior @ Warszawa",
  obSubmitting: "Tworzymy...",
  obSubmit: "Zaczynamy →",
  obSignOut: "Wyloguj",
  obFooter: "WITAJ W SYTO.",

  iaHeadingLine1: "Ktoś zaprosił",
  iaHeadingLine2: "Cię do household",
  iaIntro:
    "Wspólna lodówka, lista zakupów, przepisy. Cele i posiłki pozostają prywatne.",
  iaCodeLabel: "KOD ZAPROSZENIA",
  iaCardBody:
    "Po akceptacji zobaczysz wspólną lodówkę, listę zakupów i propozycje przepisów. Posiłki i cele każda osoba ma swoje.",
  iaJoinError: "Nie udało się dołączyć",
  iaSubmitting: "Dołączam...",
  iaSubmit: "Dołączam →",
  iaLater: "Może później",
};

const en: AuthCopy = {
  langFieldLabel: "Język · Language · Sprache",
  eyebrowSignUp: "SIGN UP",
  eyebrowSignIn: "SIGN IN",
  headingSignUp: "Welcome to syto",
  headingSignIn: "Hello again",
  nameLabel: "Name",
  namePlaceholder: "e.g. Mark",
  emailLabel: "Email",
  emailPlaceholder: "you@example.com",
  passwordLabel: "Password",
  passwordHint: "Minimum 8 characters",
  inviteCodeLabel: "Invite code",
  inviteCodeHint: "Sytofood is in closed beta. The code comes to you in person.",
  inviteCodePlaceholder: "e.g. SYTO-2026-ALPHA",
  forgotPasswordLink: "Forgot password →",
  acceptTermsPrefix: "I accept the ",
  acceptTermsPrivacy: "Privacy policy",
  acceptTermsAnd: " and ",
  acceptTermsTerms: "Terms",
  acceptTermsSuffix: ". I understand Sytofood is not a substitute for dietitian advice.",
  mustAcceptTermsError: "You need to accept the Privacy policy and Terms.",
  pendingVerification:
    "If the email is new, we have sent a verification link. If you already have an account, sign in.",
  genericError: "Something went wrong",
  submitting: "One sec...",
  submitSignUp: "Create account →",
  submitSignIn: "Sign in →",
  haveAccountQuestion: "Already have an account? ",
  noAccountQuestion: "No account yet? ",
  switchToSignIn: "Sign in",
  switchToSignUp: "Sign up",
  footerPrivacy: "PRIVACY",
  footerTerms: "TERMS",

  fpBackToSignIn: "Sign in",
  fpEyebrow: "PASSWORD RESET",
  fpHeading: "We'll send a link",
  fpIntro: "Enter the email you signed up with. The link works for 30 minutes.",
  fpSentEyebrow: "CHECK YOUR INBOX",
  fpSentHeading: "Sent",
  fpSentBodyPrefix: "If we have such an account, the reset link is on its way to ",
  fpSentBodySuffix: ". It works for 30 minutes.",
  fpHintLabel: "TIP",
  fpHintBody: "Check your spam folder too, it sometimes ends up there.",
  fpSubmitting: "Sending...",
  fpSubmit: "Send link →",
  fpBackButton: "Back to sign in →",

  rpEyebrow: "NEW PASSWORD",
  rpHeading: "Set a fresh one",
  rpIntro: "Minimum 8 characters. We'll sign you in right after you save.",
  rpNewPasswordLabel: "New password",
  rpNewPasswordHint: "Minimum 8 characters",
  rpConfirmLabel: "Repeat password",
  rpPasswordMismatch: "The passwords don't match. Check again.",
  rpSubmitting: "Saving...",
  rpSubmit: "Set password →",
  rpHintLabel: "TIP",
  rpHintBody: "Longer and non-dictionary beats hard to remember.",

  veEyebrowVerifying: "CHECKING THE CODE",
  veEyebrowOk: "EMAIL VERIFIED",
  veEyebrowError: "INVALID CODE",
  veHeadingVerifying: "One sec",
  veHeadingOk: "All set",
  veHeadingError: "That didn't work",
  veBodyVerifying: "Connecting to the server. A second, two at most.",
  veBodyOk: "Your account is active. Back to the app.",
  veBodyErrorFallback: "The code is invalid or expired. Try again.",
  veCodeInvalidFallback: "The code is invalid or expired",
  veRetryButton: "Go back →",

  obEyebrow: "WELCOME · 1/1",
  obHeadingLine1: "You eat.",
  obHeadingLine2: "You say it.",
  obHeadingLine3: "We count",
  obIntro:
    "Type, photograph or say what you eat. AI counts the kcal and macros. No scale, no spreadsheets, no ED vibe.",
  obFeatures: [
    { num: "01", title: "Fridge photo", sub: "we detect 30+ products" },
    { num: "02", title: "Menu scan", sub: "top 3 for your budget" },
    { num: "03", title: '"I feel like…"', sub: "a recipe for the rest of the day" },
  ],
  obHouseholdEyebrow: "YOUR HOUSEHOLD",
  obHouseholdIntro:
    "Sytofood works per household. Create yours, then invite your family.",
  obNameLabel: "Name",
  obNamePlaceholder: "e.g. Smith @ London",
  obSubmitting: "Creating...",
  obSubmit: "Let's start →",
  obSignOut: "Sign out",
  obFooter: "WELCOME TO SYTO.",

  iaHeadingLine1: "Someone invited",
  iaHeadingLine2: "you to a household",
  iaIntro:
    "A shared fridge, shopping list and recipes. Goals and meals stay private.",
  iaCodeLabel: "INVITE CODE",
  iaCardBody:
    "Once you accept, you'll see the shared fridge, shopping list and recipe suggestions. Everyone keeps their own meals and goals.",
  iaJoinError: "Couldn't join",
  iaSubmitting: "Joining...",
  iaSubmit: "Join →",
  iaLater: "Maybe later",
};

const de: AuthCopy = {
  langFieldLabel: "Język · Language · Sprache",
  eyebrowSignUp: "REGISTRIERUNG",
  eyebrowSignIn: "ANMELDUNG",
  headingSignUp: "Willkommen bei syto",
  headingSignIn: "Schön, dass du da bist",
  nameLabel: "Name",
  namePlaceholder: "z. B. Markus",
  emailLabel: "E-Mail",
  emailPlaceholder: "du@example.com",
  passwordLabel: "Passwort",
  passwordHint: "Mindestens 8 Zeichen",
  inviteCodeLabel: "Einladungscode",
  inviteCodeHint:
    "Sytofood ist in der geschlossenen Beta. Den Code bekommst du persönlich.",
  inviteCodePlaceholder: "z. B. SYTO-2026-ALPHA",
  forgotPasswordLink: "Passwort vergessen →",
  acceptTermsPrefix: "Ich akzeptiere die ",
  acceptTermsPrivacy: "Datenschutzerklärung",
  acceptTermsAnd: " und die ",
  acceptTermsTerms: "AGB",
  acceptTermsSuffix:
    ". Mir ist klar, dass Sytofood keine Ernährungsberatung ersetzt.",
  mustAcceptTermsError: "Du musst die Datenschutzerklärung und die AGB akzeptieren.",
  pendingVerification:
    "Wenn die E-Mail neu ist, haben wir einen Bestätigungslink geschickt. Wenn du schon ein Konto hast, melde dich an.",
  genericError: "Etwas ist schiefgelaufen",
  submitting: "Einen Moment...",
  submitSignUp: "Konto erstellen →",
  submitSignIn: "Anmelden →",
  haveAccountQuestion: "Schon ein Konto? ",
  noAccountQuestion: "Noch kein Konto? ",
  switchToSignIn: "Anmelden",
  switchToSignUp: "Registrieren",
  footerPrivacy: "DATENSCHUTZ",
  footerTerms: "AGB",

  fpBackToSignIn: "Anmeldung",
  fpEyebrow: "PASSWORT ZURÜCKSETZEN",
  fpHeading: "Wir schicken einen Link",
  fpIntro:
    "Gib die E-Mail an, mit der du dich registriert hast. Der Link gilt 30 Minuten.",
  fpSentEyebrow: "POSTEINGANG PRÜFEN",
  fpSentHeading: "Gesendet",
  fpSentBodyPrefix: "Wenn es ein solches Konto gibt, ist der Reset-Link unterwegs an ",
  fpSentBodySuffix: ". Er gilt 30 Minuten.",
  fpHintLabel: "TIPP",
  fpHintBody: "Schau auch im Spam-Ordner nach, manchmal landet er dort.",
  fpSubmitting: "Senden...",
  fpSubmit: "Link senden →",
  fpBackButton: "Zurück zur Anmeldung →",

  rpEyebrow: "NEUES PASSWORT",
  rpHeading: "Setz ein frisches",
  rpIntro: "Mindestens 8 Zeichen. Nach dem Speichern melden wir dich direkt an.",
  rpNewPasswordLabel: "Neues Passwort",
  rpNewPasswordHint: "Mindestens 8 Zeichen",
  rpConfirmLabel: "Passwort wiederholen",
  rpPasswordMismatch: "Die Passwörter stimmen nicht überein. Prüfe es nochmal.",
  rpSubmitting: "Speichern...",
  rpSubmit: "Passwort setzen →",
  rpHintLabel: "TIPP",
  rpHintBody: "Länger und nicht im Wörterbuch schlägt schwer zu merken.",

  veEyebrowVerifying: "CODE WIRD GEPRÜFT",
  veEyebrowOk: "E-MAIL BESTÄTIGT",
  veEyebrowError: "CODE UNGÜLTIG",
  veHeadingVerifying: "Einen Moment",
  veHeadingOk: "Alles bereit",
  veHeadingError: "Das hat nicht geklappt",
  veBodyVerifying: "Wir verbinden uns mit dem Server. Eine Sekunde, höchstens zwei.",
  veBodyOk: "Dein Konto ist aktiv. Zurück zur App.",
  veBodyErrorFallback: "Der Code ist ungültig oder abgelaufen. Versuch es nochmal.",
  veCodeInvalidFallback: "Der Code ist ungültig oder abgelaufen",
  veRetryButton: "Zurück →",

  obEyebrow: "WILLKOMMEN · 1/1",
  obHeadingLine1: "Du isst.",
  obHeadingLine2: "Du sagst es.",
  obHeadingLine3: "Wir zählen",
  obIntro:
    "Tippe, fotografiere oder sag, was du isst. Die KI zählt kcal und Makros. Keine Waage, keine Tabellen, kein ED-Vibe.",
  obFeatures: [
    { num: "01", title: "Kühlschrank-Foto", sub: "wir erkennen 30+ Produkte" },
    { num: "02", title: "Speisekarten-Scan", sub: "Top 3 für dein Budget" },
    { num: "03", title: '"Ich habe Lust auf…"', sub: "ein Rezept für den Rest des Tages" },
  ],
  obHouseholdEyebrow: "DEIN HAUSHALT",
  obHouseholdIntro:
    "Sytofood arbeitet pro Haushalt. Erstell deinen, dann lade die Familie ein.",
  obNameLabel: "Name",
  obNamePlaceholder: "z. B. Müller @ Berlin",
  obSubmitting: "Wird erstellt...",
  obSubmit: "Los geht's →",
  obSignOut: "Abmelden",
  obFooter: "WILLKOMMEN BEI SYTO.",

  iaHeadingLine1: "Jemand hat dich",
  iaHeadingLine2: "in einen Haushalt eingeladen",
  iaIntro:
    "Gemeinsamer Kühlschrank, Einkaufsliste, Rezepte. Ziele und Mahlzeiten bleiben privat.",
  iaCodeLabel: "EINLADUNGSCODE",
  iaCardBody:
    "Nach dem Annehmen siehst du den gemeinsamen Kühlschrank, die Einkaufsliste und Rezeptvorschläge. Mahlzeiten und Ziele hat jede Person für sich.",
  iaJoinError: "Beitritt fehlgeschlagen",
  iaSubmitting: "Beitreten...",
  iaSubmit: "Beitreten →",
  iaLater: "Vielleicht später",
};

export const authCopy: Record<Lang, AuthCopy> = { pl, en, de };
